import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sparkles, Home, HardDrive } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import StoryCard from "@/components/StoryCard";
import Navbar from "@/components/Navbar";
import PageBackground from "@/components/PageBackground";
import StoryCardSkeleton from "@/components/StoryCardSkeleton";
interface Story {
  id: string;
  title: string;
  character_name: string;
  qualities: string[];
  tempo: string;
  created_at: string;
  slug: string;
  image_url: string | null;
  thumbnail_url: string | null;
  audio_url: string | null;
}
const Library = () => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  useEffect(() => {
    checkUser();
    fetchStories();
  }, []);
  const checkUser = async () => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
    }
  };
  const fetchStories = async () => {
    try {
      // Fetch fields needed for the library list plus audio_url to check for base64
      const {
        data,
        error
      } = await supabase.from("stories").select("id, title, character_name, qualities, tempo, created_at, slug, image_url, thumbnail_url, audio_url").order("created_at", {
        ascending: false
      });
      if (error) throw error;
      setStories(data || []);
    } catch (error) {
      console.error("Error fetching stories:", error);
      toast({
        title: "Error",
        description: "Failed to load stories",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const migrateToStorage = async () => {
    setMigrating(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke("migrate-to-storage");
      if (error) throw error;
      toast({
        title: "Migration Complete",
        description: data.message
      });
      await fetchStories();
    } catch (error) {
      console.error("Error migrating to storage:", error);
      toast({
        title: "Error",
        description: "Failed to migrate data",
        variant: "destructive"
      });
    } finally {
      setMigrating(false);
    }
  };
  const hasStories = stories.length > 0;
  const storiesWithBase64 = stories.filter(s => s.image_url && s.image_url.startsWith("data:") || s.audio_url && s.audio_url.startsWith("data:")).length;
  return <PageBackground>
      <Navbar />

      <main className="container mx-auto px-4 py-8 md:py-12 pb-24 md:pb-12">
        {loading ? <div className="animate-fade-in">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground font-sans mb-6 md:mb-8">Your Stories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[...Array(6)].map((_, i) => (
                <StoryCardSkeleton key={i} />
              ))}
            </div>
          </div> : !hasStories ? <div className="max-w-3xl mx-auto text-center py-12 md:py-20 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full mb-4 md:mb-6">
              <Sparkles className="w-8 h-8 md:w-10 md:h-10 text-primary" />
            </div>
            <h2 className="text-2xl md:text-4xl font-semibold text-foreground mb-3 md:mb-4 font-serif">
              No Stories Yet
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
              Create your first story from the home page
            </p>
            <Button size="lg" onClick={() => navigate("/")} className="gap-2 h-12 md:h-11 w-full sm:w-auto">
              <Home className="w-5 h-5" />
              Go to Home
            </Button>
          </div> : <div className="animate-fade-in">
            <div className="flex flex-col gap-4 mb-6 md:mb-8">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground font-sans">Your Stories</h2>
              {storiesWithBase64 > 0 && (
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={migrateToStorage} disabled={migrating} className="gap-2 text-sm">
                    <HardDrive className="w-4 h-4" />
                    {migrating ? "Migrating..." : `Migrate ${storiesWithBase64}`}
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {stories.map(story => <StoryCard key={story.id} story={story} onClick={() => navigate(`/story/${story.slug}`)} />)}
            </div>
            </div>}
      </main>
    </PageBackground>;
};
export default Library;